// AXIOM — WebGL context acquisition
//
// `new THREE.WebGLRenderer({...})` throws if the browser refuses the context,
// and the game used to make exactly one attempt, with the most demanding option
// set it had: `powerPreference: 'high-performance'`. That is a real reason to be
// refused, not a hypothetical one — a discrete-GPU hint can be denied outright
// by a sandboxed or embedded browser, by an enterprise GPU blocklist, on a
// laptop whose switchable graphics are unavailable, or when the browser has
// already handed out its per-process context budget.
//
// The "fallback" engine then asked for the SAME thing, plus antialias, so it
// failed identically and the player got a dead loading bar with
// `Error creating WebGL context.` printed under it.
//
// So: walk down. Each rung drops a demand that can independently cause refusal,
// and the last one asks for essentially nothing. Whatever succeeds is reported,
// so a degraded context is visible rather than mysterious.

import * as THREE from 'three';

/** Progressively less demanding context requests. */
const LADDER = [
  {
    label: 'high-performance',
    opts: { antialias: false, alpha: false, stencil: false, depth: true,
      powerPreference: 'high-performance', preserveDrawingBuffer: false,
      logarithmicDepthBuffer: false },
  },
  {
    // Dropping the discrete-GPU hint alone rescues a surprising number of
    // machines: the browser is willing to give a context, just not that one.
    label: 'default power preference',
    opts: { antialias: false, alpha: false, stencil: false, depth: true,
      preserveDrawingBuffer: false, logarithmicDepthBuffer: false },
  },
  {
    label: 'low-power',
    opts: { antialias: false, alpha: false, stencil: false, depth: true,
      powerPreference: 'low-power' },
  },
  {
    // `failIfMajorPerformanceCaveat: false` is the explicit "I will take a
    // software rasteriser" signal. Slow is a game; refused is not.
    label: 'software-tolerant',
    opts: { failIfMajorPerformanceCaveat: false },
  },
];

/**
 * Build a WebGLRenderer, trying progressively softer context options.
 *
 * @param {HTMLCanvasElement} canvas
 * @param {object} [extra] merged into every attempt (e.g. `{ canvas }` extras)
 * @returns {{ renderer: THREE.WebGLRenderer, tier: string, attempts: object[] }}
 * @throws the LAST error, with a message a player can act on, if all rungs fail.
 */
export function createRenderer(canvas, extra = {}) {
  const attempts = [];
  for (const rung of LADDER) {
    try {
      const renderer = new THREE.WebGLRenderer({ canvas, ...rung.opts, ...extra });
      // Constructing can succeed while the context is already lost on some
      // drivers; treat that as a failure so the next rung gets a turn.
      const gl = renderer.getContext();
      if (!gl || gl.isContextLost()) throw new Error('context lost immediately');
      attempts.push({ tier: rung.label, ok: true });
      if (rung !== LADDER[0]) {
        console.warn(`[renderer] context acquired at reduced tier: ${rung.label}`);
      }
      return { renderer, tier: rung.label, attempts };
    } catch (e) {
      attempts.push({ tier: rung.label, ok: false, err: String(e && e.message || e) });
    }
  }

  const err = new Error(
    'WebGL を初期化できませんでした。ブラウザのハードウェアアクセラレーションが' +
    '無効になっているか、この環境では GPU を利用できません。' +
    '（Chrome: 設定 → システム → 「ハードウェア アクセラレーションが使用可能な場合は使用する」）');
  err.attempts = attempts;
  throw err;
}

export default createRenderer;
